/**
 * Tab 3 — "Add tab": Raise Presentation + Update Lender Collection
 * (Tasks 4 & 5, plan §5.5, Phase 10B).
 *
 * Backend contracts consumed (verified against backend/lms/views_staff_ops.py):
 *   GET  .../presentation/<loan_id>/eligibility/  PresentationEligibilityView:1127
 *   GET  .../presentation/<loan_id>/list/         PresentationListView:1782
 *   POST .../presentation/<loan_id>/raise_upi/    -> 202 {queued, id, ...}
 *   POST .../presentation/<loan_id>/raise_nach/   -> 202 {queued, id, ...}
 *   POST .../presentation/<loan_id>/update_lender_collection/ -> 202, or 501
 *                                                 for an unsupported lender
 *
 * Loan context is a lightweight loan-id input + "Load" button, exactly as in
 * MandateEditPanel: these endpoints are gated on `presentation_collection`
 * while the active-loans list is gated on `loan_profile`, so a loan-picker
 * table here would 403 for a user holding only presentation_collection access.
 *
 * THE 26-HOUR LOCKOUT is owned by the backend
 * (`earliest_allowed_settlement_datetime()`, views_staff_ops.py:1121 — the
 * single source of truth, re-validated on every raise at :1286). This component
 * never re-derives "+26 hours"; it only compares the staff-chosen settlement
 * instant against the server's `earliest_allowed` so the buttons reflect the
 * same rule the API will enforce.
 *
 * Settlement instants are built in **IST** because the backend localises the
 * submitted date/time with `_IST.localize(...)` (views_staff_ops.py:1247), so a
 * browser in any other timezone must not use its own local wall clock.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    Search,
    RefreshCcw,
    Send,
    Sunrise,
    Clock,
    Lock,
    Building2,
    ShieldCheck,
    ShieldX,
    AlertTriangle,
    CheckCircle2,
    Loader2,
} from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

import lmsStaffService, {
    PresentationListRow,
    PresentationType,
    RaisePresentationPayload,
    getLmsApiErrorMessage,
} from './lmsStaffApi';
import { formatDateTime, istDateString, toDateInputValue } from './formatters';

const READ_ONLY_TOOLTIP = 'You have view-only access to this tab';

/** The 6 am slot behind Task 4's "Raise ... presentation at 6 AM" button. */
const SIX_AM = '06:00';

/**
 * The exact instant represented by `<date> <time>` **in IST**, matching the
 * backend's `_IST.localize(datetime(...))`. India has no DST so the fixed
 * +05:30 offset is exact, not an approximation. Null for unparseable input.
 */
const istInstant = (dateStr: string, timeStr: string): Date | null => {
    if (!dateStr || !timeStr) return null;
    const parsed = new Date(`${dateStr}T${timeStr}:00+05:30`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** PENDING = yellow, SUCCESS = green, FAILED = red. */
const STATUS_CLASSES: Record<string, string> = {
    SUCCESS: 'border-green-200 bg-green-50 text-green-700',
    FAILED: 'border-red-200 bg-red-50 text-red-700',
    PENDING: 'border-amber-200 bg-amber-50 text-amber-700',
};

const PresentationStatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
    const value = (status || 'PENDING').toUpperCase();
    return (
        <Badge
            variant="outline"
            className={`text-xs ${
                STATUS_CLASSES[value] || 'border-gray-200 bg-gray-50 text-gray-700'
            }`}
        >
            {value}
        </Badge>
    );
};

const TypeBadge: React.FC<{ type: PresentationType | null }> = ({ type }) => (
    <Badge
        variant="outline"
        className={`text-xs ${
            type === 'NACH'
                ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
                : 'border-sky-200 bg-sky-50 text-sky-700'
        }`}
    >
        {type || '-'}
    </Badge>
);

/** Explains a disabled control instead of leaving the operator guessing. */
const GatedButton: React.FC<{
    reason: string | null;
    children: React.ReactElement;
}> = ({ reason, children }) => {
    if (!reason) return children;
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex cursor-not-allowed">{children}</span>
                </TooltipTrigger>
                <TooltipContent>{reason}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

// ---------------------------------------------------------------------------
// Raise section — rendered twice (UPI, then NACH). NACH's structure mirrors
// UPI's exactly, including its mandate dependency, per plan §5.5's explicit
// "exactly mirroring the UPI button's dependency".
// ---------------------------------------------------------------------------

interface RaiseSectionProps {
    type: PresentationType;
    canEdit: boolean;
    /** eligibility.has_active_{upi,nach}_mandate for this section's type. */
    hasActiveMandate: boolean;
    earliestAllowed: string | null;
    /** Earliest IST date that can hold any valid slot — the input's `min`. */
    minSelectableDate?: string;
    isPending: boolean;
    /**
     * True when a presentation of THIS type already SUCCEEDED for the selected
     * date — plan §5.5 then hides the raise buttons entirely and defers to that
     * presentation row's inline "Update Lender Collection" button.
     */
    alreadySucceeded: boolean;
    date: string;
    onDateChange: (value: string) => void;
    onRaise: (type: PresentationType, payload: RaisePresentationPayload) => void;
}

const RaiseSection: React.FC<RaiseSectionProps> = ({
    type,
    canEdit,
    hasActiveMandate,
    earliestAllowed,
    minSelectableDate,
    isPending,
    alreadySucceeded,
    date,
    onDateChange,
    onRaise,
}) => {
    const [showCustomTime, setShowCustomTime] = React.useState(false);
    const [time, setTime] = React.useState('10:00');

    const earliest = earliestAllowed ? new Date(earliestAllowed) : null;

    const tooEarly = (targetTime: string): boolean => {
        const target = istInstant(date, targetTime);
        return !!(target && earliest && target.getTime() < earliest.getTime());
    };

    /** Shared gating for both buttons; only the target time differs. */
    const reasonFor = (targetTime: string): string | null => {
        if (!canEdit) return READ_ONLY_TOOLTIP;
        if (!hasActiveMandate)
            return `No active ${type} mandate for this loan — nothing to present against.`;
        if (!date) return 'Pick the date to raise the presentation for.';
        if (!istInstant(date, targetTime)) return 'Enter a valid date and time.';
        if (tooEarly(targetTime))
            return `Must be at least 26 hours from now (earliest ${formatDateTime(
                earliestAllowed
            )}).`;
        return null;
    };

    const sixAmReason = reasonFor(SIX_AM);
    const customReason = reasonFor(time);

    return (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <Send className="h-4 w-4 text-gray-400" /> Raise {type} Presentation
                </h3>
                {hasActiveMandate ? (
                    <span className="flex items-center gap-1 text-xs text-green-700">
                        <ShieldCheck className="h-3.5 w-3.5" /> Active {type} mandate
                    </span>
                ) : (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                        <ShieldX className="h-3.5 w-3.5" /> No active {type} mandate
                    </span>
                )}
            </div>

            {alreadySucceeded ? (
                /* Plan §5.5 business rule — the raise buttons are REPLACED (not
                   merely disabled) once this type SUCCEEDED for this date. */
                <div className="flex items-start gap-2 rounded-lg border border-green-100 bg-green-50 p-3 text-sm text-green-800">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                        A {type} presentation for {date} has already succeeded, so raising
                        again is hidden. Use the "Update Lender Collection" button on that
                        presentation's row below.
                    </span>
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="space-y-1">
                            <Label htmlFor={`raise-date-${type}`}>Settlement date</Label>
                            <Input
                                id={`raise-date-${type}`}
                                type="date"
                                value={date}
                                onChange={(e) => onDateChange(e.target.value)}
                                disabled={!canEdit}
                                className="w-full sm:w-48"
                            />
                        </div>

                        <GatedButton reason={sixAmReason}>
                            <Button
                                type="button"
                                className="gap-2"
                                disabled={!!sixAmReason || isPending}
                                onClick={() => onRaise(type, { mode: '6am', date })}
                            >
                                {isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Sunrise className="h-4 w-4" />
                                )}
                                Raise {type} Presentation at 6 AM
                            </Button>
                        </GatedButton>
                    </div>

                    <div className="space-y-3 border-t pt-3">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id={`custom-time-${type}`}
                                checked={showCustomTime}
                                onCheckedChange={(checked) =>
                                    setShowCustomTime(checked === true)
                                }
                                disabled={!canEdit}
                            />
                            <Label
                                htmlFor={`custom-time-${type}`}
                                className="cursor-pointer text-sm"
                            >
                                Raise {type} Presentation at custom time
                            </Label>
                        </div>

                        {showCustomTime && (
                            <div className="flex flex-wrap items-end gap-3">
                                <div className="space-y-1">
                                    <Label htmlFor={`raise-time-${type}`}>Time (IST)</Label>
                                    <Input
                                        id={`raise-time-${type}`}
                                        type="time"
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                        disabled={!canEdit}
                                        className="w-full sm:w-36"
                                    />
                                </div>
                                <GatedButton reason={customReason}>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="gap-2"
                                        disabled={!!customReason || isPending}
                                        onClick={() =>
                                            onRaise(type, { mode: 'custom', date, time })
                                        }
                                    >
                                        <Clock className="h-4 w-4" /> Raise at {time} IST
                                    </Button>
                                </GatedButton>
                            </div>
                        )}
                    </div>

                    {(tooEarly(SIX_AM) || (showCustomTime && tooEarly(time))) && (
                        <p className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertTriangle className="h-3.5 w-3.5" /> Must be at least 26
                            hours from now — earliest allowed{' '}
                            {formatDateTime(earliestAllowed)}.
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export interface AddPresentationPanelProps {
    /** can_edit for `presentation_collection`, resolved by LmsStaffOpsHub. */
    canEdit: boolean;
    /** Optional deep-linked loan (set when arriving from an overview tab). */
    initialLoanId?: string | null;
}

const AddPresentationPanel: React.FC<AddPresentationPanelProps> = ({
    canEdit,
    initialLoanId,
}) => {
    const queryClient = useQueryClient();

    const [loanIdInput, setLoanIdInput] = React.useState(initialLoanId || '');
    const [loanId, setLoanId] = React.useState<string | null>(initialLoanId || null);

    /**
     * One shared settlement date across both raise sections: the plan's rule is
     * per (type, date) and staff work one settlement date at a time. Defaults to
     * two days out, since anything sooner sits inside the 26-hour lockout for
     * most of the working day.
     */
    const [date, setDate] = React.useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 2);
        return toDateInputValue(d);
    });

    // Deep-link: adopt a loan chosen on another tab.
    React.useEffect(() => {
        if (initialLoanId) {
            setLoanIdInput(initialLoanId);
            setLoanId(initialLoanId);
        }
    }, [initialLoanId]);

    const eligibilityKey = ['lms-staff-presentation-eligibility', loanId];
    const listKey = ['lms-staff-presentation-list', loanId];

    const {
        data: eligibility,
        isLoading: eligibilityLoading,
        isError: eligibilityError,
        error: eligibilityErrorObj,
    } = useQuery({
        queryKey: eligibilityKey,
        queryFn: async () => {
            const response = await lmsStaffService.getPresentationEligibility(
                loanId as string
            );
            return response.data;
        },
        enabled: !!loanId,
        // `earliest_allowed` is a moving target (now + 26h) — keep it fresh so a
        // long-lived tab cannot enable a slot the API would now reject.
        refetchInterval: 5 * 60 * 1000,
    });

    const {
        data: presentationList,
        isLoading: listLoading,
        isFetching: listFetching,
        refetch: refetchList,
    } = useQuery({
        queryKey: listKey,
        queryFn: async () => {
            const response = await lmsStaffService.getPresentationList(loanId as string);
            return response.data;
        },
        enabled: !!loanId,
        /**
         * Auto-refresh on the same 5-minute cadence as the backend's Beat task
         * `lms-staff-poll-presentation-status`, and only while something is
         * still non-final — so `last_polled_at` visibly advances without the
         * operator touching the manual Refresh button.
         */
        refetchInterval: (query) => {
            const current = query.state.data?.results ?? [];
            const hasNonFinal = current.some((row) => {
                const status = (row.final_status || 'PENDING').toUpperCase();
                return status !== 'SUCCESS' && status !== 'FAILED';
            });
            return hasNonFinal ? 5 * 60 * 1000 : false;
        },
    });

    // Wrapped in its own useMemo (rather than a bare `?? []`) so this array's
    // identity is stable across renders when `presentationList.results` is
    // unchanged -- the succeededTodayByType useMemo below depends on `rows`,
    // and a fresh `[]` literal every render would defeat that memoisation.
    const rows: PresentationListRow[] = React.useMemo(
        () => presentationList?.results ?? [],
        [presentationList]
    );

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: listKey });
        queryClient.invalidateQueries({ queryKey: eligibilityKey });
    };

    const raiseMutation = useMutation({
        mutationFn: (vars: { type: PresentationType; payload: RaisePresentationPayload }) =>
            vars.type === 'UPI'
                ? lmsStaffService.raiseUpiPresentation(loanId as string, vars.payload)
                : lmsStaffService.raiseNachPresentation(loanId as string, vars.payload),
        onSuccess: (response) => {
            const row = response.data;
            toast.success(
                `${row.presentation_type} presentation dispatched (request #${row.id}) for ` +
                    formatDateTime(row.settlement_datetime)
            );
            // 202 — the Digio call runs in a Celery worker, so `nsp_id` lands a
            // moment after this response. Mirrors MandateEditPanel's delayed
            // re-read rather than showing a permanently empty NSP ID.
            invalidateAll();
            setTimeout(invalidateAll, 5000);
        },
        onError: (error: unknown) => {
            toast.error(
                getLmsApiErrorMessage(error, 'Failed to dispatch the presentation raise')
            );
        },
    });

    const collectionMutation = useMutation({
        mutationFn: (presentationId: number) =>
            lmsStaffService.updateLenderCollection(loanId as string, presentationId),
        onSuccess: (response) => {
            toast.success(
                `Lender collection dispatched to ${
                    response.data.lender_name || 'lender'
                } for presentation #${response.data.presentation_id}`
            );
            setTimeout(invalidateAll, 5000);
        },
        onError: (error: unknown) => {
            // A 501 for an unsupported lender is a known, explanatory response
            // — surfaced verbatim so it reads as a limitation, not a bug.
            toast.error(
                getLmsApiErrorMessage(error, 'Failed to dispatch the lender collection')
            );
        },
    });

    const handleLoad = () => {
        const trimmed = loanIdInput.trim();
        if (!trimmed) {
            toast.error('Enter a loan ID to load');
            return;
        }
        setLoanId(trimmed);
    };

    const handleRaise = (type: PresentationType, payload: RaisePresentationPayload) =>
        raiseMutation.mutate({ type, payload });

    /** Earliest IST date that can hold ANY valid slot — the date input's `min`. */
    const minSelectableDate = istDateString(eligibility?.earliest_allowed) || undefined;

    /**
     * Plan §5.5: "Once final_status == SUCCESS the frontend hides Raise-buttons
     * for that date/type" — derived purely from the list response (no extra flag
     * needed), keyed on (type, IST settlement date) so a success on one date can
     * never hide raising for a different date.
     */
    const succeededTypesForDate = React.useMemo(() => {
        const succeeded = new Set<PresentationType>();
        rows.forEach((row) => {
            if (
                (row.final_status || '').toUpperCase() === 'SUCCESS' &&
                row.presentation_type &&
                istDateString(row.settlement_datetime) === date
            ) {
                succeeded.add(row.presentation_type);
            }
        });
        return succeeded;
    }, [rows, date]);

    return (
        <div className="space-y-6">
            {/* ── Loan selector ─────────────────────────────────────────── */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                        <Label htmlFor="presentation-loan-id">Loan ID</Label>
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <Input
                                id="presentation-loan-id"
                                placeholder="e.g. IL0000123"
                                value={loanIdInput}
                                onChange={(e) => setLoanIdInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleLoad();
                                }}
                                className="pl-9"
                            />
                        </div>
                    </div>
                    <Button type="button" onClick={handleLoad}>
                        Load
                    </Button>
                    {!canEdit && (
                        <span className="text-xs text-amber-600">
                            View-only access — raise and collection buttons are disabled.
                        </span>
                    )}
                </div>
            </div>

            {!loanId && (
                <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 shadow-sm">
                    Enter a loan ID above to check presentation eligibility and raise a
                    presentation.
                </div>
            )}

            {loanId && eligibilityLoading && (
                <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <Skeleton className="h-6 w-1/3" />
                    <Skeleton className="h-20 w-full" />
                </div>
            )}

            {loanId && eligibilityError && (
                <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
                    {getLmsApiErrorMessage(
                        eligibilityErrorObj,
                        'Unable to load presentation eligibility for this loan.'
                    )}
                </div>
            )}

            {loanId && !eligibilityLoading && !eligibilityError && eligibility && (
                <>
                    {/* ── Eligibility ───────────────────────────────────── */}
                    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                            <Lock className="h-4 w-4 text-gray-400" /> Presentation
                            Eligibility
                        </h3>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div>
                                <div className="text-xs uppercase tracking-wide text-gray-400">
                                    Earliest allowed (26h lockout)
                                </div>
                                <div className="text-sm font-medium text-gray-800">
                                    {formatDateTime(eligibility.earliest_allowed)}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-gray-400">
                                    Active UPI mandate
                                </div>
                                <div className="text-sm font-medium text-gray-800">
                                    {eligibility.has_active_upi_mandate ? 'Yes' : 'No'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-gray-400">
                                    Active NACH mandate
                                </div>
                                <div className="text-sm font-medium text-gray-800">
                                    {eligibility.has_active_nach_mandate ? 'Yes' : 'No'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Raise UPI, then the mirrored NACH section ──────── */}
                    <RaiseSection
                        type="UPI"
                        canEdit={canEdit}
                        hasActiveMandate={eligibility.has_active_upi_mandate}
                        earliestAllowed={eligibility.earliest_allowed}
                        minSelectableDate={minSelectableDate}
                        isPending={raiseMutation.isPending}
                        alreadySucceeded={succeededTypesForDate.has('UPI')}
                        date={date}
                        onDateChange={setDate}
                        onRaise={handleRaise}
                    />

                    <RaiseSection
                        type="NACH"
                        canEdit={canEdit}
                        hasActiveMandate={eligibility.has_active_nach_mandate}
                        earliestAllowed={eligibility.earliest_allowed}
                        minSelectableDate={minSelectableDate}
                        isPending={raiseMutation.isPending}
                        alreadySucceeded={succeededTypesForDate.has('NACH')}
                        date={date}
                        onDateChange={setDate}
                        onRaise={handleRaise}
                    />

                    {/* ── Presentation list ─────────────────────────────── */}
                    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                                <Send className="h-4 w-4 text-gray-400" /> Presentation
                                Requests
                            </h3>
                            <div className="flex items-center gap-3">
                                {listFetching && !listLoading && (
                                    <span className="text-xs text-gray-400">
                                        Refreshing...
                                    </span>
                                )}
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="gap-2"
                                    onClick={() => refetchList()}
                                >
                                    <RefreshCcw className="h-4 w-4" /> Refresh
                                </Button>
                            </div>
                        </div>

                        <p className="text-xs text-gray-500">
                            Non-final statuses are polled by the backend every 5 minutes
                            (Beat task poll_pending_presentation_status_task); "last
                            updated" below shows when each row was last checked, so
                            refreshing only pulls those updates in sooner.
                        </p>

                        {listLoading ? (
                            <Skeleton className="h-24 w-full" />
                        ) : rows.length === 0 ? (
                            <div className="text-sm text-gray-500">
                                No presentation requests recorded for this loan yet.
                            </div>
                        ) : (
                            <ul className="space-y-3">
                                {rows.map((row) => {
                                    const status = (row.final_status || 'PENDING').toUpperCase();
                                    const isSuccess = status === 'SUCCESS';
                                    const isFinal = isSuccess || status === 'FAILED';
                                    return (
                                        <li
                                            key={row.id}
                                            className="space-y-3 rounded-lg border border-gray-200 p-3"
                                        >
                                            <div className="flex flex-wrap items-center gap-2">
                                                <TypeBadge type={row.presentation_type} />
                                                <PresentationStatusBadge
                                                    status={row.final_status}
                                                />
                                                <span className="text-xs text-gray-500">
                                                    #{row.id}
                                                </span>
                                                <span className="break-all font-mono text-xs text-gray-700">
                                                    NSP: {row.nsp_id || '—'}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-3">
                                                <div>
                                                    <div className="uppercase tracking-wide text-gray-400">
                                                        Settlement
                                                    </div>
                                                    <div className="text-gray-700">
                                                        {formatDateTime(
                                                            row.settlement_datetime
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="uppercase tracking-wide text-gray-400">
                                                        Status finalised
                                                    </div>
                                                    <div className="text-gray-700">
                                                        {row.status_finalized_at
                                                            ? formatDateTime(
                                                                  row.status_finalized_at
                                                              )
                                                            : 'not final yet'}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="uppercase tracking-wide text-gray-400">
                                                        {isFinal
                                                            ? 'Last polled'
                                                            : 'Last updated (auto-poll)'}
                                                    </div>
                                                    <div className="text-gray-700">
                                                        {formatDateTime(
                                                            row.last_polled_at ||
                                                                row.updated_at
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Nested LmsLenderCollectionAudit history */}
                                            {row.collection_audits.length > 0 && (
                                                <div className="space-y-1 border-t pt-2">
                                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                        Lender collection history
                                                    </div>
                                                    <ul className="space-y-1 text-xs">
                                                        {row.collection_audits.map(
                                                            (audit) => (
                                                                <li
                                                                    key={audit.id}
                                                                    className="flex flex-wrap items-center gap-2"
                                                                >
                                                                    <Badge
                                                                        variant="outline"
                                                                        className={
                                                                            audit.is_success
                                                                                ? 'border-green-200 bg-green-50 text-green-700'
                                                                                : 'border-red-200 bg-red-50 text-red-700'
                                                                        }
                                                                    >
                                                                        {audit.is_success
                                                                            ? 'SUCCESS'
                                                                            : 'FAILED'}
                                                                    </Badge>
                                                                    <span className="text-gray-600">
                                                                        {formatDateTime(
                                                                            audit.created_at
                                                                        )}
                                                                    </span>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                </div>
                                            )}

                                            {/*
                                             * Plan §5.5 business rule, per-presentation
                                             * half: a SUCCEEDED presentation offers
                                             * "Update Lender Collection" inline on ITS OWN
                                             * row (never as a separate global button),
                                             * while the matching raise section above hides
                                             * its buttons for that type + date.
                                             */}
                                            {isSuccess && (
                                                <div className="border-t pt-2">
                                                    <GatedButton
                                                        reason={
                                                            canEdit ? null : READ_ONLY_TOOLTIP
                                                        }
                                                    >
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            className="gap-2"
                                                            disabled={
                                                                !canEdit ||
                                                                collectionMutation.isPending
                                                            }
                                                            onClick={() =>
                                                                collectionMutation.mutate(
                                                                    row.id
                                                                )
                                                            }
                                                        >
                                                            {collectionMutation.isPending ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                <Building2 className="h-4 w-4" />
                                                            )}
                                                            Update Lender Collection
                                                        </Button>
                                                    </GatedButton>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AddPresentationPanel;
